import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

const PURPLE = "#6C50BF";
const PURPLE_DARK = "#492ba3";

function SectionHeader({
  icon: Icon,
  title,
  count,
  unreadCount = 0,
  color,
  expanded,
  onToggle,
  actions,
}) {
  return (
    <ListItemButton
      onClick={onToggle}
      sx={{
        py: 1,
        px: 2,
        minHeight: 48,
        borderRadius: 0,
        bgcolor: expanded ? `${color}08` : "transparent",
        borderBottom: "1px solid #eef0f6",
        transition: "background-color 0.2s ease",
        "&:hover": { bgcolor: `${color}12` },
      }}
    >
      <Box sx={{ position: "relative", mr: 1.25, flexShrink: 0, display: "flex" }}>
        <Icon sx={{ fontSize: 18, color }} />
        {unreadCount > 0 && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: -3,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#ef4444",
              border: "1.5px solid #fff",
              boxShadow: "0 0 0 0 rgba(239,68,68,0.55)",
              animation: "chatUnreadPulse 1.6s ease-out infinite",
              "@keyframes chatUnreadPulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(239,68,68,0.55)" },
                "70%": { boxShadow: "0 0 0 6px rgba(239,68,68,0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(239,68,68,0)" },
              },
            }}
          />
        )}
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                color,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {title}
            </Typography>
            {count > 0 && (
              <Chip
                label={count}
                size="small"
                sx={{
                  height: 18,
                  minWidth: 20,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  bgcolor: `${color}14`,
                  color,
                  "& .MuiChip-label": { px: 0.65 },
                }}
              />
            )}
            {unreadCount > 0 && (
              <Chip
                label={unreadCount > 99 ? "99+" : unreadCount}
                size="small"
                aria-label={`${unreadCount} unread`}
                sx={{
                  height: 18,
                  minWidth: 20,
                  fontSize: "0.62rem",
                  fontWeight: 800,
                  bgcolor: "#ef4444",
                  color: "#fff",
                  "& .MuiChip-label": { px: 0.65 },
                }}
              />
            )}
          </Box>
        }
      />
      {actions}
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          color: "#94a3b8",
          ml: 0.5,
          flexShrink: 0,
          transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        }}
      />
    </ListItemButton>
  );
}

function ContactRow({ item, isSelected, onClick, accent, subtitle, online, isBot, unreadCount = 0 }) {
  const label = item.name || item.groupName || "—";
  const initial = label.replace(/^#\s*/, "").charAt(0).toUpperCase();
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 0.9,
        px: 2,
        pl: 2.25,
        mx: 0.75,
        mb: 0.25,
        borderRadius: "10px",
        borderLeft: isSelected ? `3px solid ${accent}` : "3px solid transparent",
        bgcolor: isSelected ? `${accent}10` : hasUnread ? "rgba(239,68,68,0.06)" : "transparent",
        "&:hover": {
          bgcolor: isSelected ? `${accent}16` : hasUnread ? "rgba(239,68,68,0.10)" : "rgba(0,0,0,0.03)",
        },
        transition: "background-color 0.15s ease",
      }}
    >
      <Box sx={{ position: "relative", mr: 1.25, flexShrink: 0 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            background: `linear-gradient(135deg, ${accent}, ${PURPLE_DARK})`,
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          {initial}
        </Avatar>
        {online != null && (
          <Box
            aria-label={online ? "online" : "offline"}
            sx={{
              position: "absolute", right: -1, bottom: -1, width: 11, height: 11,
              borderRadius: "50%", border: "2px solid #fff",
              bgcolor: online ? "#22c55e" : "#c7cad6",
            }}
          />
        )}
        {hasUnread && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "#ef4444",
              border: "2px solid #fff",
            }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: hasUnread || isSelected ? 800 : 500,
              fontSize: "0.875rem",
              color: "#1a1a2e",
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          {isBot && (
            <Box component="span" sx={{ flexShrink: 0, fontSize: "0.6rem", fontWeight: 700, letterSpacing: 0.4, color: "#6C50BF", bgcolor: "#EDE8FA", px: 0.5, py: "1px", borderRadius: "4px", lineHeight: 1.4 }}>
              BOT
            </Box>
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: hasUnread ? "#3f3f55" : "#8b8fa3",
            fontSize: "0.75rem",
            fontWeight: hasUnread ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mt: 0.1,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      {hasUnread && (
        <Box
          aria-label={`${unreadCount} unread`}
          sx={{
            ml: 0.75,
            minWidth: 20,
            height: 20,
            px: 0.6,
            borderRadius: "10px",
            bgcolor: "#ef4444",
            color: "#fff",
            fontSize: "0.68rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          {badgeLabel}
        </Box>
      )}
    </ListItemButton>
  );
}

const scrollSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": { width: "5px" },
  "&::-webkit-scrollbar-thumb": {
    background: "#d8dce6",
    borderRadius: "10px",
    "&:hover": { background: "#c4c9d4" },
  },
};

const Sideboard = ({
  employees,
  groups,
  onSelectChat,
  selectedChatId,
  canCreateGroup = false,
  onCreateGroup,
  onBrowseChannels,
  onOpenSearch,
  onlineSet,
  statuses = {},
  myStatus,
  onEditStatus,
  dmUnread = {},
  groupUnread = {},
  dmPreviews = {},
  groupPreviews = {},
}) => {
  const isOnline = (emp) => !!(onlineSet && emp?.employeeid && onlineSet.has(String(emp.employeeid)));
  const statusOf = (emp) => (emp?.employeeid ? statuses[String(emp.employeeid)] : null);
  const [showGroups, setShowGroups] = useState(true);
  const [showIndividualChats, setShowIndividualChats] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const filteredEmployees = employees.filter((emp) =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = groups.filter((grp) =>
    (grp.name || grp.groupName || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const dmUnreadTotal = filteredEmployees.reduce(
    (sum, e) => sum + (dmUnread[String(e._id)] || 0),
    0
  );
  const groupUnreadTotal = filteredGroups.reduce(
    (sum, g) => sum + (groupUnread[String(g._id)] || 0),
    0
  );
  const totalUnread = dmUnreadTotal + groupUnreadTotal;

  const jumpToUnread = () => {
    if (!totalUnread) return;
    const firstDm = filteredEmployees.find((e) => (dmUnread[String(e._id)] || 0) > 0);
    if (firstDm) {
      setShowIndividualChats(true);
      onSelectChat(firstDm._id, "dm");
      return;
    }
    const firstGroup = filteredGroups.find((g) => (groupUnread[String(g._id)] || 0) > 0);
    if (firstGroup) {
      setShowGroups(true);
      onSelectChat(firstGroup._id, "group");
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 1.75,
          pt: 1.5,
          pb: 1.25,
          background: `linear-gradient(135deg, ${PURPLE} 0%, ${PURPLE_DARK} 100%)`,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={() => navigate("/EmployeeDashboard")}
              size="small"
              sx={{ color: "#fff", p: 0.75, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
              Chats
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mr: { xs: 7, md: 0 } }}>
            <Tooltip title={totalUnread ? `${totalUnread} unread message${totalUnread === 1 ? "" : "s"} — click to open` : "No new messages"}>
              <IconButton
                onClick={jumpToUnread}
                size="small"
                aria-label={totalUnread ? `${totalUnread} unread messages` : "No new messages"}
                sx={{ color: "#fff", p: 0.75, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                <Badge
                  badgeContent={totalUnread}
                  color="error"
                  max={99}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: 800,
                      fontSize: "0.62rem",
                      minWidth: 16,
                      height: 16,
                      bgcolor: "#ef4444",
                    },
                  }}
                >
                  {totalUnread > 0 ? (
                    <NotificationsActiveIcon fontSize="small" />
                  ) : (
                    <NotificationsNoneOutlinedIcon fontSize="small" />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Search messages">
              <IconButton
                onClick={() => onOpenSearch?.()}
                size="small"
                aria-label="Search messages"
                sx={{ color: "#fff", p: 0.75, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "rgba(255,255,255,0.2)",
            borderRadius: "10px",
            px: 1.25,
            py: 0.35,
          }}
        >
          <SearchIcon sx={{ color: "rgba(255,255,255,0.75)", fontSize: 18, mr: 0.75 }} />
          <InputBase
            placeholder="Filter people & channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              color: "#fff",
              fontSize: "0.82rem",
              "& ::placeholder": { color: "rgba(255,255,255,0.65)", opacity: 1 },
            }}
          />
        </Box>
      </Box>

      {/* Stacked accordion — expanding DM pushes Channels down; whole list scrolls */}
      <Box sx={scrollSx}>
        {/* Direct Messages */}
        <Box component="section" aria-label="Direct Messages">
          <SectionHeader
            icon={ChatBubbleOutlineIcon}
            title="Direct Messages"
            count={filteredEmployees.length}
            unreadCount={dmUnreadTotal}
            color={PURPLE}
            expanded={showIndividualChats}
            onToggle={() => setShowIndividualChats((v) => !v)}
          />
          <Collapse in={showIndividualChats} timeout={280} easing="cubic-bezier(0.4, 0, 0.2, 1)">
            <List component="div" disablePadding sx={{ py: 0.5 }}>
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee) => {
                  const bot = String(employee.employeeid || "").startsWith("BOT-");
                  const unread = dmUnread[String(employee._id)] || 0;
                  const preview = dmPreviews[String(employee._id)];
                  return (
                  <ContactRow
                    key={employee._id}
                    item={employee}
                    isBot={bot}
                    isSelected={String(selectedChatId) === String(employee._id)}
                    onClick={() => onSelectChat(employee._id, "dm")}
                    accent={PURPLE}
                    online={bot ? null : isOnline(employee)}
                    unreadCount={unread}
                    subtitle={
                      preview
                        ? preview
                        : statusOf(employee)?.text
                        ? `${statusOf(employee).emoji ? statusOf(employee).emoji + " " : ""}${statusOf(employee).text}`
                        : (employee.designation || "Team Member")
                    }
                  />
                );})
              ) : (
                <Typography
                  variant="body2"
                  sx={{ px: 2.5, py: 1.5, color: "#8b8fa3", fontSize: "0.8rem", textAlign: "center" }}
                >
                  No contacts found
                </Typography>
              )}
            </List>
          </Collapse>
        </Box>

        <Divider sx={{ borderColor: "#eef0f6", my: 0.5 }} />

        {/* Channels */}
        <Box component="section" aria-label="Channels">
          <SectionHeader
            icon={GroupsIcon}
            title="Channels"
            count={filteredGroups.length}
            unreadCount={groupUnreadTotal}
            color={PURPLE_DARK}
            expanded={showGroups}
            onToggle={() => setShowGroups((v) => !v)}
            actions={
              <>
                <Tooltip title="Browse public channels">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBrowseChannels?.();
                    }}
                    sx={{ color: PURPLE_DARK, p: 0.5, mr: 0.25 }}
                    aria-label="Browse public channels"
                  >
                    <TravelExploreIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                {canCreateGroup && (
                  <Tooltip title="New channel">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateGroup?.();
                      }}
                      sx={{ color: PURPLE_DARK, p: 0.5, mr: 0.25 }}
                      aria-label="New channel"
                    >
                      <AddIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            }
          />
          <Collapse in={showGroups} timeout={280} easing="cubic-bezier(0.4, 0, 0.2, 1)">
            <List component="div" disablePadding sx={{ py: 0.5, pb: 1.5 }}>
              {filteredGroups.length > 0 ? (
                filteredGroups.map((group) => {
                  const baseName = group.name || group.groupName || "Group";
                  const displayName =
                    group.visibility === "public" ? `# ${baseName}` : baseName;
                  const unread = groupUnread[String(group._id)] || 0;
                  const preview = groupPreviews[String(group._id)] || group.lastMessage;
                  return (
                    <ContactRow
                      key={group._id}
                      item={{ ...group, name: displayName }}
                      isSelected={String(selectedChatId) === String(group._id)}
                      onClick={() => onSelectChat(group._id, "group")}
                      accent={PURPLE_DARK}
                      unreadCount={unread}
                      subtitle={preview || "Group chat"}
                    />
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  sx={{ px: 2.5, py: 1.5, color: "#8b8fa3", fontSize: "0.8rem", textAlign: "center" }}
                >
                  No channels yet
                </Typography>
              )}
            </List>
          </Collapse>
        </Box>
      </Box>
      {/* My custom status (click to edit) */}
      {onEditStatus && (
        <Box
          onClick={onEditStatus}
          role="button"
          sx={{
            flexShrink: 0, px: 1.75, py: 1.1, borderTop: "1px solid #eef0f6",
            display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
            "&:hover": { bgcolor: "rgba(108,80,191,0.06)" },
          }}
        >
          <Box sx={{ fontSize: 18, lineHeight: 1 }}>{myStatus?.emoji || "😀"}</Box>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: "0.8rem", color: myStatus?.text ? "#1a1a2e" : "#8b8fa3", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {myStatus?.text || "Set a status"}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "#b8bcc8", flexShrink: 0 }}>✎</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Sideboard;
